import React from "react";
import PropTypes from "prop-types";

// UI
import { Button as UIButton, buttonVariants } from "../../ui/button";

function stopEvent(event, { preventDefault, stopPropagation }) {
  if (!event) return;

  if (preventDefault && typeof event.preventDefault === "function") {
    event.preventDefault();
  }

  if (stopPropagation && typeof event.stopPropagation === "function") {
    event.stopPropagation();
  }
}

function wrapHandler(handler, options) {
  const { stopPropagation, preventDefault, onlyKeys } = options;

  if (!handler && !stopPropagation && !preventDefault) return undefined;

  return (event) => {
    const shouldStopForKey =
      Array.isArray(onlyKeys) && event && typeof event.key === "string"
        ? onlyKeys.includes(event.key)
        : true;

    if (shouldStopForKey) {
      stopEvent(event, { preventDefault, stopPropagation });
    }

    if (handler) handler(event);
  };
}

const Button = React.forwardRef(function Button(
  {
    stopPropagation = false,
    preventDefault = false,
    onClick,
    onKeyDown,
    onMouseDown,
    onPointerDown,
    disabled,
    asChild = false,
    ...props
  },
  ref
) {
  const content = (
    <UIButton
      ref={ref}
      asChild={asChild}
      disabled={disabled}
      onClick={wrapHandler(onClick, { stopPropagation, preventDefault })}
      onMouseDown={wrapHandler(onMouseDown, { stopPropagation, preventDefault })}
      onPointerDown={wrapHandler(onPointerDown, { stopPropagation, preventDefault })}
      onKeyDown={wrapHandler(onKeyDown, {
        stopPropagation,
        preventDefault,
        onlyKeys: ["Enter", " "],
      })}
      {...props}
    />
  );

  if (disabled && !asChild && (stopPropagation || preventDefault)) {
    const wrapperStop = (event) => {
      stopEvent(event, {
        stopPropagation: true,
        preventDefault,
      });
    };

    return (
      <span
        className="inline-flex cursor-not-allowed"
        onClick={wrapperStop}
        onMouseDown={wrapperStop}
        onPointerDown={wrapperStop}
      >
        {content}
      </span>
    );
  }

  return content;
});

Button.displayName = "Button";

Button.propTypes = {
  stopPropagation: PropTypes.bool,
  preventDefault: PropTypes.bool,
  onClick: PropTypes.func,
  onKeyDown: PropTypes.func,
  onMouseDown: PropTypes.func,
  onPointerDown: PropTypes.func,
  disabled: PropTypes.bool,
  asChild: PropTypes.bool,
};

export { Button, buttonVariants };
